import { trialEndPlanSelectionModalOpenAtom } from '@/atoms/trialAtoms';
import {
  DowngradeReconsiderModalVariant,
  downgradeReconsiderModalStateAtom,
} from '@/components/modal/downgrade-reconsider-modal';
import { pricingPlansModalOpenAtom } from '@/features/settings/billing/billingAtoms';
import { useSubscriptionUpgrade } from '@/features/settings/billing/hooks/useSubscriptionUpgrade';
import {
  getNewProductTierProduct,
  getPlanFromPeriod,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { type Period } from '@heidi/commerce/contracts';
import {
  ModalId,
  SignupSource,
  SubscriptionStatus,
  SubscriptionTier,
} from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

export function useTrialEndPlanSelectionModal(period: Period) {
  const [isOpen, setIsOpen] = useAtom(trialEndPlanSelectionModalOpenAtom);
  const { isOpen: isQueueOpen, handleClose: handleQueueClose } = useQueuedModal(
    ModalId.TRIAL_END_PLAN_SELECTION
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (isQueueOpen) {
      handleQueueClose();
    }
  }, [setIsOpen, isQueueOpen, handleQueueClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setIsOpen(false);
        if (isQueueOpen) {
          handleQueueClose();
        }
      } else {
        setIsOpen(true);
      }
    },
    [setIsOpen, isQueueOpen, handleQueueClose]
  );

  const shouldBeOpen = isOpen || isQueueOpen;
  const { hasTrialExpired: hasEvidenceTrialExpired } = useEvidenceBillingInfo();
  const { data: signupSource } = useQueryUser((user) => user?.signup_source);
  const setDowngradeReconsiderModalState = useSetAtom(downgradeReconsiderModalStateAtom);
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const { data: previousStatusBeforeClinicianTrial } = useQueryUserBillingInfo<
    SubscriptionStatus | null | undefined
  >((billingInfo) => billingInfo?.previous_status_before_clinician_trial ?? undefined);

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });
  const { data: products, catalogueVersion } = useQueryProducts({
    heidi2Catalogue: true,
  });

  const currentPlan: SubscriptionTier | null = useMemo(() => {
    // Evidence Plus retired (GRO-3635) — expired Evidence signup → Clinician plan picker.
    if (hasEvidenceTrialExpired && signupSource === SignupSource.evidence) {
      return SubscriptionTier.CLINICIAN_PRO;
    }
    switch (previousStatusBeforeClinicianTrial) {
      case SubscriptionStatus.PRO:
      case SubscriptionStatus.PRO_TRIAL:
        return SubscriptionTier.CLINICIAN_PRO;
      case SubscriptionStatus.PRO_PLUS:
      case SubscriptionStatus.PRO_PLUS_TRIAL:
        return SubscriptionTier.CLINICIAN_PRO_PLUS;
      default:
        return SubscriptionTier.CLINICIAN_PRO;
    }
  }, [hasEvidenceTrialExpired, signupSource, previousStatusBeforeClinicianTrial]);

  const handleViewAllPlans = useCallback(() => {
    handleClose();
    setPricingPlansModalOpen(true);
  }, [handleClose, setPricingPlansModalOpen]);

  const handleDowngradeToFree = useCallback(() => {
    if (currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS) {
      setDowngradeReconsiderModalState({
        open: true,
        variant: DowngradeReconsiderModalVariant.Practice,
      });
      return;
    }
    if (currentPlan === SubscriptionTier.CLINICIAN_PRO) {
      setDowngradeReconsiderModalState({
        open: true,
        variant: DowngradeReconsiderModalVariant.Clinician,
      });
      return;
    }
    handleClose();
  }, [handleClose, setDowngradeReconsiderModalState, currentPlan]);

  const handleClinicianCheckout = useCallback(
    () =>
      void handleUpgrade(
        period,
        SubscriptionTier.CLINICIAN_PRO,
        catalogueVersion === 'v3'
          ? (getPlanFromPeriod(
              period,
              getNewProductTierProduct(SubscriptionTier.CLINICIAN_PRO, products)
            )?.id ?? null)
          : undefined
      ),
    [catalogueVersion, handleUpgrade, period, products]
  );

  const handlePracticeCheckout = useCallback(
    () =>
      void handleUpgrade(
        period,
        SubscriptionTier.CLINICIAN_PRO_PLUS,
        catalogueVersion === 'v3'
          ? (getPlanFromPeriod(
              period,
              getNewProductTierProduct(SubscriptionTier.CLINICIAN_PRO_PLUS, products)
            )?.id ?? null)
          : undefined
      ),
    [catalogueVersion, handleUpgrade, period, products]
  );

  /** Plans to show based on trial: Clinician = 2 options, Practice = 3 options */
  const visiblePlans = useMemo((): SubscriptionTier[] => {
    if (currentPlan === SubscriptionTier.CLINICIAN_PRO) {
      if (previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO)
        return [SubscriptionTier.PRO, SubscriptionTier.CLINICIAN_PRO];
      return [SubscriptionTier.FREE, SubscriptionTier.CLINICIAN_PRO];
    }
    if (currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS) {
      if (previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO_PLUS)
        return [SubscriptionTier.PRO_PLUS, SubscriptionTier.CLINICIAN_PRO_PLUS];
      return [
        SubscriptionTier.FREE,
        SubscriptionTier.CLINICIAN_PRO,
        SubscriptionTier.CLINICIAN_PRO_PLUS,
      ];
    }
    return [SubscriptionTier.FREE, SubscriptionTier.CLINICIAN_PRO];
  }, [currentPlan, previousStatusBeforeClinicianTrial]);

  return {
    handleDowngradeToFree,
    handleClinicianCheckout,
    handlePracticeCheckout,
    handleClose,
    handleViewAllPlans,
    handleOpenChange,
    shouldBeOpen,
    currentPlan,
    visiblePlans,
    isPending,
    isAppleCompliant: isAppleCompliantBuild(),
  };
}
